import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  Map, CheckCircle2, Wrench, ArrowRight, Sparkles, Mail,
  Music, Users, ShieldCheck, TrendingUp, Filter,
} from "lucide-react";

/**
 * MeloCraft — Public Roadmap
 * -----------------------------------------------
 * Living Kanban board of the product. Public on purpose: it builds trust
 * with artists and fans to see the trajectory. Every column is an editable
 * const below — as items ship, move them from NEXT → BUILDING → SHIPPED.
 *
 * Tags: For fans, For artists, For admin, Growth. Filter chips at top.
 */

const TAGS = {
  fans: { label: "For Fans", icon: Users, color: "#7DD3FC" },
  artists: { label: "For Artists", icon: Music, color: "#FF4500" },
  admin: { label: "For Admin", icon: ShieldCheck, color: "#A78BFA" },
  growth: { label: "Growth", icon: TrendingUp, color: "#34D399" },
};

const SHIPPED = [
  { title: "Artist marketplace", desc: "Sell songs for $0.99 and albums for $15 direct to fans.", tag: "artists", when: "Feb 2026" },
  { title: "Free trending radio", desc: "Every artist's top tracks stream on the home page, no login.", tag: "fans", when: "Feb 2026" },
  { title: "Stripe Connect payouts", desc: "Artists get paid direct. 100% of every sale (minus Stripe fees) — MeloCraft platform fee is $0.", tag: "artists", when: "Feb 2026" },
  { title: "$2/mo artist plan", desc: "Unlocks selling. Up to 150 tracks per artist. Founder bypasses all limits.", tag: "artists", when: "Feb 2026" },
  { title: "Track & album uploads", desc: "MP3/WAV/FLAC support, iOS-safe file picker, cover art per release, per-single cover for standalones.", tag: "artists", when: "Feb 2026" },
  { title: "Album Manager + drag reorder", desc: "Big cards + checkbox picker to bundle singles into albums, drag-and-drop track order, 15-track cap.", tag: "artists", when: "Feb 2026" },
  { title: "Free album toggle", desc: "One switch flips an entire album (and all its tracks) to free download — bold FREE ribbon everywhere it appears.", tag: "artists", when: "Feb 2026" },
  { title: "Featured singles + featured album", desc: "Admin curates 1 album + up to 5 tracks on the homepage.", tag: "admin", when: "Feb 2026" },
  { title: "Daily message banner", desc: "Admin-set message ribbon at the top of the home page — talk directly to your artists.", tag: "admin", when: "Feb 2026" },
  { title: "Likes on tracks + albums", desc: "Heart button with real counts; fans can save their liked list.", tag: "fans", when: "Feb 2026" },
  { title: "AI Social Kit", desc: "One-click AI-written posts for Instagram, Facebook, X, TikTok + 3 bio lengths, per track or album. Copy-paste ready.", tag: "growth", when: "Feb 2026" },
  { title: "Tips + Follows", desc: "Fans can tip any amount (100% to artist) and follow artists for drop alerts.", tag: "fans", when: "Feb 2026" },
  { title: "Email suite (Resend)", desc: "Verification, password reset, purchase receipts, weekly digest.", tag: "growth", when: "Feb 2026" },
  { title: "Growth channels", desc: "New drops auto-post to Discord + Telegram webhooks; RSS feed; referral program.", tag: "growth", when: "Feb 2026" },
  { title: "Admin console + display-name rename", desc: "User/track management, sales stats, featured toggles, admin can rename any artist.", tag: "admin", when: "Feb 2026" },
  { title: "Legal pages", desc: "Terms of Service, Privacy Policy, DMCA takedown, Contact.", tag: "admin", when: "Feb 2026" },
  { title: "User Manual", desc: "Searchable guide for fans, artists, and admin — admin chapter auto-hidden from public.", tag: "fans", when: "Feb 2026" },
  { title: "Melo Mondays", desc: "Every artist picks one song to give away free every Monday. Radio flips to a free-picks-only reel.", tag: "growth", when: "Feb 2026" },
  { title: "Rebrand to MeloCraft", desc: "New name, tagline (The Artist-First Market), logo (breakout note), and full brand kit + print-ready assets.", tag: "growth", when: "Feb 2026" },
  { title: "Auth hardening", desc: "httpOnly cookie auth, XSS-resistant. All secrets in env, no hardcoded creds.", tag: "admin", when: "Feb 2026" },
  { title: "Printful merch (per-artist OAuth)", desc: "Each artist connects their own Printful — orders route to their account, MeloCraft never touches fulfillment. Site-branded storefront on /merch.", tag: "artists", when: "Feb 2026" },
  { title: "Explicit content flag", desc: "Artist toggle + red 'E' badge site-wide.", tag: "artists", when: "Feb 2026" },
  { title: "40-genre catalog", desc: "Including gospel, worship, christian, blues, soul, funk, bluegrass, americana, afrobeats, lo-fi, hyperpop and more.", tag: "artists", when: "Feb 2026" },
  { title: "Live preview audition", desc: "Hear your exact 30-second preview window before saving, with a 30-second progress bar.", tag: "artists", when: "Feb 2026" },
  { title: "Just Released + Live stats strip", desc: "Homepage shows last-7-days drops + real-time artist/track/play counters for instant social proof.", tag: "growth", when: "Feb 2026" },
  { title: "Rich social share prefills", desc: "Copy-caption button on artist and album pages generates ready-to-post copy for FB, X, IG, TikTok.", tag: "growth", when: "Feb 2026" },
  { title: "Community Chat (The Green Room)", desc: "Live WebSocket chat with two rooms — Everyone (all signed-in) and Artist Lounge (paid artists + admin). AI moderation, emoji reactions, pinned announcements, artist track drops, @mention notifications.", tag: "fans", when: "Feb 2026" },
  { title: "Release Notes broadcast", desc: "Every republish auto-drafts a friendly update via AI, publishes it to /releases, and emails all opted-in users. One-click unsubscribe.", tag: "growth", when: "Feb 2026" },
  { title: "21-Day Album Rollout Planner", desc: "AI drafts a dated 9-step post plan for every album drop — teaser, launch, spotlights, BTS, follow-ups. Each step has a suggested platform and one-click Copy+Open shortcut.", tag: "growth", when: "Feb 2026" },
  { title: "Multi-variant social captions", desc: "The AI Social Kit now writes 3 tonally-different drafts per platform (Question / Story / Punchline) so artists pick the hook that fits.", tag: "growth", when: "Feb 2026" },
  { title: "AI Mastering (free forever)", desc: "Every artist can master any track in one click. Smart chain (Pedalboard) auto-tunes to genre; Studio mode (Matchering) matches to a reference. No limits, no cost — the whole point of MeloCraft.", tag: "artist-tools", when: "Feb 2026" },
  { title: "Per-page social previews", desc: "Shared artist / album / track links on Facebook, X, LinkedIn, WhatsApp, Slack and Discord now show the actual cover art, title, and artist — not a generic MeloCraft preview.", tag: "growth", when: "Feb 2026" },
];

const BUILDING = [
  // Empty for now — items appear here when actively being coded.
];

const NEXT = [
  { title: "Physical media — CDs + Vinyl (Prodigi)", desc: "Sell physical CDs & vinyl on every album page. Print-on-demand via Prodigi API. Stripe Connect splits at checkout so artists get their cut instantly, no manual payouts from you. Waiting on business account setup + Prodigi sandbox key. Full build plan saved to /app/memory/prodigi_build_plan.md.", tag: "artists", eta: "~4-6 hrs after key" },
  { title: "Auto-generated share posters", desc: "Every track gets an Instagram-story-sized poster (cover + title + price + URL). One-click share to IG.", tag: "growth", eta: "~2h" },
  { title: "Live 'On Air Right Now' ticker", desc: "Homepage feed of what's being played and bought site-wide — real-time social proof.", tag: "growth", eta: "~1h" },
  { title: "First 100 Superfan badges", desc: "Fans who buy an album in its first 7 days get a permanent gold badge + 20% off future releases.", tag: "fans", eta: "~1h" },
  { title: "Printful order tracking", desc: "Webhook receiver → fans see 'shipped + tracking #' in their library.", tag: "fans", eta: "~2h" },
  { title: "Wishlist ♥", desc: "Fans save tracks and albums for later so they come back to buy.", tag: "fans", eta: "~1.5h" },
  { title: "Melo Mondays gallery page", desc: "Dedicated /melo-mondays page listing every artist's free Monday drop in one grid.", tag: "fans", eta: "~1h" },
];

const LATER = [
  { title: "Track Stories (voice notes)", desc: "30–60s artist commentary that plays after each track — Bandcamp has text liner notes, nobody has audio.", tag: "artists" },
  { title: "Album trailer videos", desc: "Short teaser video/audio per album, autoplays on share cards.", tag: "artists" },
  { title: "Crowd-priced albums", desc: "Album price drops as more people buy ($15 → $10 → $5). Retroactive refunds for early buyers.", tag: "fans" },
  { title: "Live listen-along rooms", desc: "Artist hosts a sync-play listening party with chat for a new drop.", tag: "artists" },
  { title: "Playlists", desc: "Fans curate and share playlists across artists.", tag: "fans" },
  { title: "Comments & reviews", desc: "Feedback on tracks and albums.", tag: "fans" },
  { title: "Pre-orders", desc: "Schedule an album to drop on a future date; fans buy in advance.", tag: "artists" },
  { title: "Search + advanced filters on Browse", desc: "Full-text search, filter by genre/mood/price.", tag: "fans" },
  { title: "Custom domain support per artist", desc: "yourname.melocraft.org profile pages.", tag: "artists" },
  { title: "Mobile app (PWA)", desc: "Installable, background audio, offline downloads for buyers.", tag: "fans" },
  { title: "Fan tipping tiers", desc: "Recurring monthly support like Patreon (still 100% to artist).", tag: "fans" },
];

const COLUMNS = [
  { id: "shipped", title: "Shipped", subtitle: "Live in production", icon: CheckCircle2, accent: "#10B981", items: SHIPPED },
  { id: "building", title: "Building Now", subtitle: "In active development", icon: Wrench, accent: "#FF4500", items: BUILDING },
  { id: "next", title: "Next Up", subtitle: "Queued, in priority order", icon: ArrowRight, accent: "#7DD3FC", items: NEXT },
  { id: "later", title: "Later / Ideas", subtitle: "On the horizon", icon: Sparkles, accent: "#A78BFA", items: LATER },
];

function ItemCard({ item }) {
  const tag = TAGS[item.tag];
  const TagIcon = tag?.icon;
  return (
    <article
      data-testid={`roadmap-item-${item.title.replace(/\s+/g, "-").toLowerCase()}`}
      className="bg-[#121212] border border-[#2A2A2A] hover:border-[#FF4500]/40 transition-colors p-4"
    >
      <h3 className="font-display font-bold text-base leading-tight mb-1">{item.title}</h3>
      <p className="font-mono text-[11px] text-gray-400 leading-relaxed mb-3">{item.desc}</p>
      <div className="flex items-center justify-between gap-2">
        {tag && (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest" style={{ color: tag.color }}>
            <TagIcon className="h-3 w-3" />
            {tag.label}
          </div>
        )}
        {(item.when || item.eta) && (
          <div className="font-mono text-[9px] uppercase tracking-widest text-gray-600">
            {item.when || item.eta}
          </div>
        )}
      </div>
    </article>
  );
}

function Column({ col, filter }) {
  const Icon = col.icon;
  const items = filter === "all" ? col.items : col.items.filter((i) => i.tag === filter);
  return (
    <section data-testid={`roadmap-col-${col.id}`} className="min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-8 w-8 grid place-items-center" style={{ background: `${col.accent}15`, border: `1px solid ${col.accent}40` }}>
          <Icon className="h-4 w-4" style={{ color: col.accent }} />
        </div>
        <div>
          <h2 className="font-display font-black text-lg tracking-tight leading-none">{col.title}</h2>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-gray-500 mt-1">{col.subtitle}</div>
        </div>
        <div className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500">
          {items.length}
        </div>
      </div>
      <div className="h-[2px] w-full mb-3" style={{ background: `linear-gradient(90deg, ${col.accent}, transparent)` }} />
      <div className="space-y-2">
        {items.map((i) => <ItemCard key={i.title} item={i} />)}
        {items.length === 0 && (
          <div className="border border-dashed border-[#2A2A2A] p-6 text-center font-mono text-[10px] uppercase tracking-widest text-gray-600">
            {col.id === "building" ? "Nothing in flight right now" : "No items"}
          </div>
        )}
      </div>
    </section>
  );
}

const FILTERS = [
  { id: "all", label: "All" },
  { id: "fans", label: "For Fans" },
  { id: "artists", label: "For Artists" },
  { id: "admin", label: "For Admin" },
  { id: "growth", label: "Growth" },
];

export default function Roadmap() {
  const [filter, setFilter] = useState("all");

  // Total counts for the header
  const total = useMemo(() => SHIPPED.length + BUILDING.length + NEXT.length + LATER.length, []);

  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 pb-32">
      {/* Header */}
      <div className="border-b border-[#2A2A2A] pb-8 mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-3 flex items-center gap-2">
          <Map className="h-4 w-4" /> Product Roadmap
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter">
          Where we're <span className="text-[#FF4500]">headed</span>.
        </h1>
        <p className="font-mono text-sm text-gray-400 mt-4 max-w-2xl leading-relaxed">
          A living view of MeloCraft. What's live, what's cooking, and what's next.
          Nothing here is set in stone — priorities shift with what artists and fans actually need.
        </p>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-600 mt-3">
          {total} items tracked · updated as we ship
        </div>

        {/* Filter chips */}
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${
                filter === f.id
                  ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                  : "border-[#2A2A2A] text-gray-400 hover:text-white hover:border-white/30"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {f.id !== "all" && <Filter className="h-3 w-3" />}
                {f.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        {COLUMNS.map((c) => <Column key={c.id} col={c} filter={filter} />)}
      </div>

      {/* Ask for a feature */}
      <div className="mt-14 border border-[#2A2A2A] bg-[#121212] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2 flex items-center gap-2">
            <Mail className="h-4 w-4" /> Missing something?
          </div>
          <div className="font-display font-black text-2xl md:text-3xl tracking-tight">
            Tell us what to build next.
          </div>
          <p className="font-mono text-xs text-gray-400 mt-2 max-w-xl">
            Every feature above started with an artist or fan telling us it was missing. Yours is welcome too.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href="mailto:Marcus_nelson_7@live.com?subject=MeloCraft feature request"
            data-testid="roadmap-request-btn"
            className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors inline-flex items-center gap-2"
          >
            <Mail className="h-4 w-4" /> Request a feature
          </a>
          <Link
            to="/manual"
            className="rounded-none border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors"
          >
            Read the manual
          </Link>
        </div>
      </div>
    </div>
  );
}
